import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import Can from "../../components/can";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import Tooltip from "@material-ui/core/Tooltip";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Chip from "@material-ui/core/Chip";
import { makeStyles } from "@material-ui/core/styles";
import { ConfirmDialog } from "../../components/confirm-dialog";
import {
  fetchTermsTypes,
  createTermsType,
  updateTermsType,
  deleteTermsType,
  fetchTermsVersions,
  createTermsVersion,
  fetchMyTermsOverview,
  acceptTerms,
  fetchTermsVersionRemovalImpact,
  withdrawTermsVersion,
} from "../../redux/organization/actions";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import { checkPermission } from "../../rbac";
import { alertAdd } from "../../redux/actions";
import { getLoginRedirectPath } from "../../redux/common";

const useStyles = makeStyles({
  table: {
    minWidth: 650,
  },
  section: {
    marginBottom: 32,
  },
  form: {
    marginBottom: 16,
  },
});

const TermsManagement = () => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const termsTypes = useSelector((s) => s.termsTypes);
  const termsVersions = useSelector((s) => s.termsVersions);
  const myTermsOverview = useSelector((s) => s.myTermsOverview);
  const roles = useSelector((s) => s.roles);
  const canManage = checkPermission("terms-management:manage", roles);

  // Type form state
  const [typeName, setTypeName] = useState("");
  const [typeCode, setTypeCode] = useState("");
  const [typeRequired, setTypeRequired] = useState(false);
  const [editingTypeId, setEditingTypeId] = useState(null);

  // Version form state
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [versionName, setVersionName] = useState("");
  const [versionEffectiveFrom, setVersionEffectiveFrom] = useState("");
  const [versionFile, setVersionFile] = useState(null);

  const [idToRemove, setIdToRemove] = useState(null);

  // Version withdrawal modal state
  const [withdrawState, setWithdrawState] = useState(null);
  const [withdrawReason, setWithdrawReason] = useState("");
  const [withdrawReactivate, setWithdrawReactivate] = useState(true);
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);

  const openWithdrawModal = async (versionId) => {
    setWithdrawReason("");
    setWithdrawReactivate(true);
    const result = await dispatch(fetchTermsVersionRemovalImpact(versionId));
    if (result && result.ok) {
      setWithdrawState(result.data);
    } else {
      dispatch(
        alertAdd({
          text: (result && result.message) || "Nie udalo sie wczytac informacji o wycofaniu",
          isError: true,
        })
      );
    }
  };

  const closeWithdrawModal = () => {
    setWithdrawState(null);
    setWithdrawReason("");
    setWithdrawReactivate(true);
    setWithdrawSubmitting(false);
  };

  const submitWithdraw = async () => {
    if (!withdrawState) return;
    setWithdrawSubmitting(true);
    const result = await dispatch(
      withdrawTermsVersion(withdrawState.versionId, {
        reason: withdrawReason || null,
        reactivatePrevious: withdrawState.active && withdrawReactivate,
      })
    );
    setWithdrawSubmitting(false);
    if (result && result.ok) {
      dispatch(
        alertAdd({
          text: "Wersja regulaminu wycofana",
          isSuccess: true,
        })
      );
      closeWithdrawModal();
      if (selectedTypeId) {
        dispatch(fetchTermsVersions(selectedTypeId));
      }
      dispatch(fetchMyTermsOverview());
    } else {
      dispatch(
        alertAdd({
          text: (result && result.message) || "Nie udalo sie wycofac wersji regulaminu",
          isError: true,
        })
      );
    }
  };

  useEffect(() => {
    dispatch(fetchMyTermsOverview());
    if (canManage) {
      dispatch(fetchTermsTypes());
    }
  }, [dispatch, canManage]);

  const handleAcceptOverviewItem = async (termsVersionId) => {
    await dispatch(acceptTerms(termsVersionId));
    dispatch(fetchMyTermsOverview());
  };

  useEffect(() => {
    if (selectedTypeId) {
      dispatch(fetchTermsVersions(selectedTypeId));
    }
  }, [selectedTypeId, dispatch]);

  const resetTypeForm = () => {
    setTypeName("");
    setTypeCode("");
    setTypeRequired(false);
    setEditingTypeId(null);
  };

  const TERMS_TYPE_ERROR_MESSAGES = {
    "terms-type-code-exists": "Typ regulaminu o tym kodzie juz istnieje",
  };

  const handleTypeSubmit = async (e) => {
    e.preventDefault();
    const payload = { name: typeName, code: typeCode, required: typeRequired };

    const isEdit = Boolean(editingTypeId);
    const result = await dispatch(
      isEdit ? updateTermsType(editingTypeId, payload) : createTermsType(payload)
    );

    if (result && result.ok) {
      dispatch(
        alertAdd({
          text: isEdit ? "Zapisano typ regulaminu" : "Dodano typ regulaminu",
          isSuccess: true,
        })
      );
      resetTypeForm();
      dispatch(fetchTermsTypes());
    } else {
      const code = result && result.code;
      const fallback = isEdit
        ? "Nie udalo sie zapisac typu regulaminu"
        : "Nie udalo sie dodac typu regulaminu";
      dispatch(
        alertAdd({
          text: TERMS_TYPE_ERROR_MESSAGES[code] || fallback,
          isError: true,
        })
      );
    }
  };

  const handleEditType = (type) => {
    setTypeName(type.name);
    setTypeCode(type.code);
    setTypeRequired(type.required);
    setEditingTypeId(type.id);
  };

  const handleDeleteType = async (id) => {
    const result = await dispatch(deleteTermsType(id));
    setIdToRemove(null);
    if (result && result.ok) {
      dispatch(alertAdd({ text: "Usunięto typ regulaminu", isSuccess: true }));
      dispatch(fetchTermsTypes());
    } else {
      dispatch(
        alertAdd({
          text: (result && result.message) || "Nie udało się usunąć typu regulaminu",
          isError: true,
        })
      );
    }
  };

  const TERMS_ERROR_MESSAGES = {
    "terms-file-not-pdf": "Regulamin musi być plikiem PDF (.pdf)",
    "terms-file-read-error": "Nie udało się odczytać pliku regulaminu",
    "terms-file-required": "Plik regulaminu (PDF) jest wymagany",
  };

  const handleVersionSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      termsTypeId: selectedTypeId,
      name: versionName,
      effectiveFrom: versionEffectiveFrom,
      file: versionFile,
    };

    const result = await dispatch(createTermsVersion(payload));
    if (result && result.ok) {
      dispatch(alertAdd({ text: "Wersja regulaminu zapisana", isSuccess: true }));
      setVersionName("");
      setVersionEffectiveFrom("");
      setVersionFile(null);
      dispatch(fetchTermsVersions(selectedTypeId));
    } else {
      const code = result && result.code;
      dispatch(
        alertAdd({
          text: TERMS_ERROR_MESSAGES[code] || "Nie udało się zapisać wersji regulaminu",
          isError: true,
        })
      );
    }
  };

  const pendingItems = (myTermsOverview || []).filter((t) => !t.accepted);
  const acceptedItems = (myTermsOverview || []).filter((t) => t.accepted);

  const page = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          Regulaminy
        </Typography>
      </Grid>

      {/* User Overview Section */}
      <Grid item xs={12} className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Do akceptacji
        </Typography>
        <TableContainer component={Paper}>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Typ</TableCell>
                <TableCell>Wersja</TableCell>
                <TableCell>Obowiązuje od</TableCell>
                <TableCell>Wymagany</TableCell>
                <TableCell>Dokument</TableCell>
                <TableCell align="right">Akcja</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingItems.map((item) => (
                <TableRow key={item.termsVersionId}>
                  <TableCell>
                    {item.termsTypeName}{" "}
                    <Typography variant="caption" color="textSecondary">
                      ({item.termsTypeCode})
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {item.versionName} (v{item.versionNumber})
                  </TableCell>
                  <TableCell>{item.effectiveFrom}</TableCell>
                  <TableCell>
                    {item.required ? (
                      <Chip label="Wymagany" color="secondary" size="small" />
                    ) : (
                      <Chip label="Opcjonalny" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {item.filePath && (
                      <a
                        href={`/org-media/files${item.filePath}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.filename || "Pobierz PDF"}
                      </a>
                    )}
                    {!item.filePath && item.filename && (
                      <span>{item.filename}</span>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() =>
                        handleAcceptOverviewItem(item.termsVersionId)
                      }
                    >
                      Akceptuj
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {pendingItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Brak regulaminów do akceptacji
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      <Grid item xs={12} className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Zaakceptowane
        </Typography>
        <TableContainer component={Paper}>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Typ</TableCell>
                <TableCell>Wersja</TableCell>
                <TableCell>Obowiązuje od</TableCell>
                <TableCell>Zaakceptowano</TableCell>
                <TableCell>Dokument</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {acceptedItems.map((item) => (
                <TableRow key={item.termsVersionId}>
                  <TableCell>
                    {item.termsTypeName}{" "}
                    <Typography variant="caption" color="textSecondary">
                      ({item.termsTypeCode})
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {item.versionName} (v{item.versionNumber})
                  </TableCell>
                  <TableCell>{item.effectiveFrom}</TableCell>
                  <TableCell>
                    {item.acceptedAt
                      ? new Date(item.acceptedAt).toLocaleString("pl-PL")
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {item.filePath && (
                      <a
                        href={`/org-media/files${item.filePath}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.filename || "Pobierz PDF"}
                      </a>
                    )}
                    {!item.filePath && item.filename && (
                      <span>{item.filename}</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {acceptedItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Brak zaakceptowanych regulaminów
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      {canManage && (
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom>
            Zarządzanie regulaminami
          </Typography>
        </Grid>
      )}

      {/* Terms Types Section */}
      {canManage && (
      <Grid item xs={12} className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Typy regulaminów
        </Typography>

        <Box className={classes.form}>
          <form onSubmit={handleTypeSubmit}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  required
                  label="Nazwa"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={typeName}
                  onChange={(e) => setTypeName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  required
                  label="Kod"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={typeCode}
                  onChange={(e) => setTypeCode(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={typeRequired}
                      onChange={(e) => setTypeRequired(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Wymagany"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={!typeName || !typeCode}
                >
                  {editingTypeId ? "Zapisz" : "Dodaj typ"}
                </Button>
              </Grid>
              {editingTypeId && (
                <Grid item xs={12} sm={2}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={resetTypeForm}
                  >
                    Anuluj
                  </Button>
                </Grid>
              )}
            </Grid>
          </form>
        </Box>

        {idToRemove !== null && (
          <ConfirmDialog
            text="Czy na pewno usunąć ten typ regulaminu?"
            yesAction={() => handleDeleteType(idToRemove)}
            noAction={() => setIdToRemove(null)}
          />
        )}

        <TableContainer component={Paper}>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Lp.</TableCell>
                <TableCell>Nazwa</TableCell>
                <TableCell>Kod</TableCell>
                <TableCell>Wymagany</TableCell>
                <TableCell align="right">Opcje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {termsTypes.map((type, index) => (
                <TableRow key={type.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{type.name}</TableCell>
                  <TableCell>{type.code}</TableCell>
                  <TableCell>
                    {type.required ? (
                      <Chip label="Tak" color="secondary" size="small" />
                    ) : (
                      <Chip label="Nie" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edytuj">
                      <IconButton
                        size="small"
                        onClick={() => handleEditType(type)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Usuń">
                      <IconButton
                        size="small"
                        onClick={() => setIdToRemove(type.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {termsTypes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Brak typów regulaminów
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      )}

      {/* Terms Versions Section */}
      {canManage && (
      <Grid item xs={12} className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Wersje regulaminów
        </Typography>

        <Box className={classes.form}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Wybierz typ regulaminu</InputLabel>
                <Select
                  value={selectedTypeId}
                  onChange={(e) => setSelectedTypeId(e.target.value)}
                  label="Wybierz typ regulaminu"
                >
                  {termsTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name} ({type.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {selectedTypeId && (
          <>
            <Box className={classes.form} mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Dodaj nową wersję
              </Typography>
              <form onSubmit={handleVersionSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      required
                      label="Nazwa wersji"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={versionName}
                      onChange={(e) => setVersionName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      required
                      label="Data wejścia w życie"
                      variant="outlined"
                      size="small"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={versionEffectiveFrom}
                      onChange={(e) => setVersionEffectiveFrom(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      required
                      label="Plik (PDF)"
                      variant="outlined"
                      size="small"
                      type="file"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ accept: "application/pdf,.pdf" }}
                      onChange={(e) => {
                        const f = e.target.files.length > 0 ? e.target.files[0] : null;
                        if (f && !/\.pdf$/i.test(f.name)) {
                          dispatch(
                            alertAdd({
                              text: "Regulamin musi być plikiem PDF (.pdf)",
                              isError: true,
                            })
                          );
                          e.target.value = "";
                          setVersionFile(null);
                          return;
                        }
                        setVersionFile(f);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                      disabled={!versionName || !versionEffectiveFrom || !versionFile}
                    >
                      Dodaj wersję
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Box>

            <TableContainer component={Paper}>
              <Table className={classes.table} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Wersja</TableCell>
                    <TableCell>Nazwa</TableCell>
                    <TableCell>Od</TableCell>
                    <TableCell>Do</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Link</TableCell>
                    <TableCell align="right">Akcje</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {termsVersions.map((version) => (
                    <TableRow key={version.id}>
                      <TableCell>v{version.version}</TableCell>
                      <TableCell>{version.name}</TableCell>
                      <TableCell>{version.effectiveFrom}</TableCell>
                      <TableCell>{version.effectiveTo || "-"}</TableCell>
                      <TableCell>
                        {version.active ? (
                          <Chip
                            label="Aktywny"
                            color="primary"
                            size="small"
                          />
                        ) : (
                          <Chip label="Nieaktywny" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        {version.filePath && (
                          <a
                            href={`/org-media/files${version.filePath}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {version.filename || "Pobierz PDF"}
                          </a>
                        )}
                        {!version.filePath && version.filename && (
                          <span>{version.filename}</span>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          onClick={() => openWithdrawModal(version.id)}
                        >
                          Wycofaj
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {termsVersions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Brak wersji dla wybranego typu
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Grid>
      )}

      <Dialog open={Boolean(withdrawState)} onClose={closeWithdrawModal} maxWidth="sm" fullWidth>
        <DialogTitle>Wycofanie wersji regulaminu</DialogTitle>
        <DialogContent>
          {withdrawState && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                {withdrawState.termsTypeName} — {withdrawState.versionName} (v
                {withdrawState.versionNumber})
              </Typography>

              <Typography variant="body2" gutterBottom>
                Liczba akceptacji: <b>{withdrawState.acceptanceCount}</b>
                {withdrawState.acceptanceCount > 0 &&
                  " — rekordy zostaną zachowane jako dowód historyczny."}
              </Typography>

              {withdrawState.active && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Wersja jest obecnie aktywna. Po wycofaniu zniknie z list
                  „do akceptacji" oraz „zaakceptowane".
                  {withdrawState.termsTypeRequired &&
                    !withdrawState.hasOlderActiveCandidate &&
                    " Typ regulaminu jest wymagany — bez aktywnej wersji nowi użytkownicy nie będą mogli niczego zaakceptować, dopóki nie wgrasz nowej."}
                </Typography>
              )}

              {withdrawState.active && withdrawState.hasOlderActiveCandidate && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={withdrawReactivate}
                      onChange={(e) => setWithdrawReactivate(e.target.checked)}
                    />
                  }
                  label={`Reaktywuj poprzednią wersję (v${withdrawState.olderActiveCandidateRevision})`}
                />
              )}

              <TextField
                label="Powód (opcjonalny)"
                variant="outlined"
                size="small"
                fullWidth
                multiline
                rows={2}
                value={withdrawReason}
                onChange={(e) => setWithdrawReason(e.target.value)}
                inputProps={{ maxLength: 1024 }}
                style={{ marginTop: 16 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeWithdrawModal} disabled={withdrawSubmitting}>
            Anuluj
          </Button>
          <Button
            onClick={submitWithdraw}
            color="secondary"
            variant="contained"
            disabled={withdrawSubmitting}
          >
            Wycofaj wersję
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );

  const redirect = () => <Redirect to={getLoginRedirectPath()} />;

  return <Can permission="profilePage:view" ok={page} not={redirect} />;
};

export default TermsManagement;
